import React, { useState, useRef, useEffect } from "react";
import ChatHeader from "./ChatHeader";
import MessageList from "./MessageList";
import MessageInput from "./MessageInput";
import "./ChatWindow.css";

const ChatWindow = ({
  isMinimized,
  messages,
  isTyping,
  isConnected,
  onSendMessage,
  onMinimize,
  onMaximize,
  onClose,
  config,
  currentTheme,
  onToggleTheme,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [attachments, setAttachments] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isTyping]);

  // Focus input when window opens
  useEffect(() => {
    if (!isMinimized && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isMinimized]);

  const handleSend = () => {
    if (inputValue.trim() || attachments.length > 0) {
      onSendMessage(inputValue, attachments);
      setInputValue("");
      setAttachments([]);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileUpload = (files) => {
    if (!config.allowFileUpload) return;

    const newAttachments = Array.from(files).map((file) => ({
      id: Date.now() + Math.random(),
      name: file.name,
      size: file.size,
      type: file.type,
      file,
    }));

    setAttachments((prev) => [...prev, ...newAttachments]);
  };

  const removeAttachment = (attachmentId) => {
    setAttachments((prev) => prev.filter((att) => att.id !== attachmentId));
  };

  // Debug logging for mobile
  React.useEffect(() => {
    const isMobile = window.innerWidth <= 480;
    if (isMobile) {
      console.log("📱 ChatWindow component rendered on mobile");
      console.log(
        "📱 Window dimensions:",
        window.innerWidth,
        "x",
        window.innerHeight
      );
      console.log("📱 isMinimized:", isMinimized);
    }
  }, [isMinimized]);

  return (
    <div
      className={`chatbot-window ${isMinimized ? "minimized" : ""}`}
      style={{
        // Force visibility on mobile for debugging
        ...(window.innerWidth <= 480 && {
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 2147483647,
          background: "var(--background)",
          display: "flex",
          flexDirection: "column",
        }),
      }}
    >
      <ChatHeader
        title={config.title}
        logoUrl={config.logoUrl}
        isConnected={isConnected}
        isMinimized={isMinimized}
        onMinimize={onMinimize}
        onMaximize={onMaximize}
        onClose={onClose}
        config={config}
        currentTheme={currentTheme}
        onToggleTheme={onToggleTheme}
      />

      <div className="chatbot-content">
        <MessageList
          messages={messages}
          isTyping={isTyping}
          showTimestamp={config.showTimestamp}
          welcomeMessage={config.welcomeMessage}
        />
        <div ref={messagesEndRef} />
      </div>

      <MessageInput
        ref={inputRef}
        value={inputValue}
        onChange={setInputValue}
        onSend={handleSend}
        onKeyPress={handleKeyPress}
        onFileUpload={handleFileUpload}
        attachments={attachments}
        onRemoveAttachment={removeAttachment}
        placeholder={config.placeholder}
        maxLength={config.maxMessageLength}
        allowFileUpload={config.allowFileUpload}
        disabled={!isConnected}
      />
    </div>
  );
};

export default React.forwardRef((props, ref) => (
  <ChatWindow {...props} ref={ref} />
));
